import { useQuery } from "@tanstack/react-query";
import { rag_v1 } from "../utils/axios-v2";

export interface UsagePlanResource {
  resource: string;
  limit: number;
  rollover_enabled: boolean;
  hard_limit: number | null;
  rate_limit: number | null;
  rate_limit_window: string | null;
  warning_threshold_pct: number | null;
}

export interface UsagePlan {
  code: string;
  name: string;
  description: string;
  included_feature?: string[] | null;
  resources: UsagePlanResource[];
  is_active: boolean;
  price: number;
  commitment_term: string;
  version: number;
  is_inference_only: boolean;
  is_visible_to_terminal_user: boolean;
  purchase_method: string;
}

const isAnnualPlan = (plan: UsagePlan) =>
  plan.commitment_term.trim().toUpperCase() === "ANNUAL";

const isVisibleSalesPlan = (plan: UsagePlan) =>
  plan.is_visible_to_terminal_user === true &&
  plan.purchase_method.trim().toLowerCase() === "sales";

export function getInferenceOnlyPlans(plans: UsagePlan[]): UsagePlan[] {
  return plans
    .filter(
      (plan) =>
        plan.is_active &&
        plan.is_inference_only &&
        isAnnualPlan(plan) &&
        isVisibleSalesPlan(plan),
    )
    .sort((a, b) => a.price - b.price);
}

export function getFullAccessPlans(plans: UsagePlan[]): UsagePlan[] {
  const hiddenCodes = new Set(["default", "default_limited"]);

  return plans
    .filter(
      (plan) =>
        plan.is_active &&
        !plan.is_inference_only &&
        isVisibleSalesPlan(plan) &&
        isAnnualPlan(plan) &&
        plan.price > 0 &&
        !hiddenCodes.has(plan.code),
    )
    .sort((a, b) => a.price - b.price);
}

const fetchUsagePlans = async (): Promise<UsagePlan[]> => {
  const response = await rag_v1.get<UsagePlan[]>("/usage/plans/", {
    timeout: 15000,
  });

  if (!Array.isArray(response.data)) {
    throw new Error("invalid_usage_plans_response");
  }

  return response.data;
};

export const useUsagePlans = (enabled = true) =>
  useQuery({
    queryKey: ["usage", "plans", "available"],
    queryFn: fetchUsagePlans,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
